import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Text,
  Badge,
  Select,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import 'react-quill/dist/quill.snow.css';

const LazyReactQuill = lazy(() => import('react-quill'));

const CLOZE_STYLES_ID = 'cloze-blank-styles';
const CLOZE_STYLES = `
  .cloze-blank {
    display: inline-block;
    background: #eeedfe;
    color: #3c3489;
    border: 1px solid #574feb;
    border-radius: 4px;
    padding: 0 8px;
    margin: 0 2px;
    font-weight: 500;
    font-size: 0.9em;
    line-height: 1.6;
    user-select: none;
    vertical-align: baseline;
  }
`;

let blotRegistered = false;
let registerPromise = null;

function ensureClozeBlotRegistered() {
  if (blotRegistered) return Promise.resolve();
  if (registerPromise) return registerPromise;
  registerPromise = import('react-quill').then((mod) => {
    const QuillModule = mod.Quill || (mod.default && mod.default.Quill);
    if (!QuillModule || blotRegistered) return;
    const Embed = QuillModule.import('blots/embed');
    class ClozeBlankBlot extends Embed {
      static create(value) {
        const node = super.create();
        node.setAttribute('data-blank-id', String(value));
        node.setAttribute('contenteditable', 'false');
        node.textContent = String(value);
        return node;
      }
      static value(node) {
        return node.getAttribute('data-blank-id');
      }
    }
    ClozeBlankBlot.blotName = 'cloze-blank';
    ClozeBlankBlot.tagName = 'span';
    ClozeBlankBlot.className = 'cloze-blank';
    QuillModule.register(ClozeBlankBlot, true);
    blotRegistered = true;
  });
  return registerPromise;
}

function ensureStylesInjected() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(CLOZE_STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = CLOZE_STYLES_ID;
  style.textContent = CLOZE_STYLES;
  document.head.appendChild(style);
}

function normalizeBlank(b) {
  return {
    id: b.id,
    mode: b.mode === 'select' ? 'select' : 'text',
    accepted_answers: Array.isArray(b.accepted_answers) ? b.accepted_answers : [],
    options: Array.isArray(b.options) ? b.options : null,
  };
}

function nextAvailableId(blanks) {
  const used = new Set(blanks.map((b) => Number(b.id)));
  let n = 1;
  while (used.has(n)) n += 1;
  return String(n);
}

function parseCsv(s) {
  return String(s || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

function extractPresentIds(editor) {
  const ids = new Set();
  if (!editor) return ids;
  const contents = editor.getContents();
  if (!contents || !contents.ops) return ids;
  contents.ops.forEach((op) => {
    if (op.insert && typeof op.insert === 'object' && op.insert['cloze-blank']) {
      ids.add(String(op.insert['cloze-blank']));
    }
  });
  return ids;
}

function blotIndexById(editor, id) {
  if (!editor) return -1;
  const contents = editor.getContents();
  let index = 0;
  for (const op of contents.ops || []) {
    if (op.insert && typeof op.insert === 'object' && op.insert['cloze-blank']) {
      if (String(op.insert['cloze-blank']) === String(id)) return index;
      index += 1;
    } else if (typeof op.insert === 'string') {
      index += op.insert.length;
    } else {
      index += 1;
    }
  }
  return -1;
}

export default function ClozeEditor({ value, onChange }) {
  const quillRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [promptHtml, setPromptHtml] = useState(value?.prompt_html || '');
  const [blanks, setBlanks] = useState((value?.blanks || []).map(normalizeBlank));
  const blanksRef = useRef(blanks);
  blanksRef.current = blanks;

  useEffect(() => {
    ensureStylesInjected();
    ensureClozeBlotRegistered().then(() => setIsReady(true));
  }, []);

  useEffect(() => {
    setPromptHtml(value?.prompt_html || '');
    setBlanks((value?.blanks || []).map(normalizeBlank));
  }, [value?.prompt_html]);

  const emit = (nextHtml, nextBlanks) => {
    onChange({
      version: 1,
      prompt_html: nextHtml,
      blanks: nextBlanks,
    });
  };

  const handleHtmlChange = (html, _delta, _source, editor) => {
    const presentIds = extractPresentIds(editor);
    const current = blanksRef.current;
    const nextBlanks = current.filter((b) => presentIds.has(String(b.id)));
    setPromptHtml(html);
    if (nextBlanks.length !== current.length) {
      setBlanks(nextBlanks);
    }
    emit(html, nextBlanks);
  };

  const handleMarkBlank = () => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const range = editor.getSelection();
    if (!range || range.length === 0) {
      alert('Selecciona una palabra del texto antes de marcarla como espacio.');
      return;
    }
    const selected = editor.getText(range.index, range.length).trim();
    if (!selected) {
      alert('La selección está vacía.');
      return;
    }
    const id = nextAvailableId(blanksRef.current);
    editor.deleteText(range.index, range.length, 'user');
    editor.insertEmbed(range.index, 'cloze-blank', id, 'user');
    editor.setSelection(range.index + 1, 0, 'user');
    const newBlank = normalizeBlank({ id, accepted_answers: [selected] });
    const nextBlanks = [...blanksRef.current, newBlank];
    setBlanks(nextBlanks);
    const nextHtml = editor.root.innerHTML;
    setPromptHtml(nextHtml);
    emit(nextHtml, nextBlanks);
  };

  const handleChangeMode = (id, nextMode) => {
    const nextBlanks = blanksRef.current.map((b) => {
      if (b.id !== id) return b;
      if (nextMode === 'select') {
        const seed = (b.accepted_answers && b.accepted_answers[0]) || '';
        return {
          ...b,
          mode: 'select',
          options: b.options && b.options.length ? b.options : seed ? [seed] : [],
          accepted_answers: seed ? [seed] : [],
        };
      }
      return {
        ...b,
        mode: 'text',
        options: null,
      };
    });
    setBlanks(nextBlanks);
    emit(promptHtml, nextBlanks);
  };

  const handleUpdateTextAnswers = (id, csv) => {
    const nextBlanks = blanksRef.current.map((b) =>
      b.id === id ? { ...b, accepted_answers: parseCsv(csv) } : b
    );
    setBlanks(nextBlanks);
    emit(promptHtml, nextBlanks);
  };

  const handleUpdateOptionText = (id, optionIdx, nextText) => {
    const nextBlanks = blanksRef.current.map((b) => {
      if (b.id !== id) return b;
      const opts = [...(b.options || [])];
      const prevOpt = opts[optionIdx];
      opts[optionIdx] = nextText;
      const wasCorrect =
        b.accepted_answers && b.accepted_answers[0] === prevOpt;
      return {
        ...b,
        options: opts,
        accepted_answers: wasCorrect ? [nextText] : b.accepted_answers,
      };
    });
    setBlanks(nextBlanks);
    emit(promptHtml, nextBlanks);
  };

  const handleAddOption = (id) => {
    const nextBlanks = blanksRef.current.map((b) => {
      if (b.id !== id) return b;
      const opts = [...(b.options || []), ''];
      return { ...b, options: opts };
    });
    setBlanks(nextBlanks);
    emit(promptHtml, nextBlanks);
  };

  const handleRemoveOption = (id, optionIdx) => {
    const nextBlanks = blanksRef.current.map((b) => {
      if (b.id !== id) return b;
      const opts = [...(b.options || [])];
      const removed = opts.splice(optionIdx, 1);
      const wasCorrect =
        b.accepted_answers && b.accepted_answers[0] === removed[0];
      return {
        ...b,
        options: opts,
        accepted_answers: wasCorrect ? [] : b.accepted_answers,
      };
    });
    setBlanks(nextBlanks);
    emit(promptHtml, nextBlanks);
  };

  const handleSetCorrect = (id, optionIdx) => {
    const nextBlanks = blanksRef.current.map((b) => {
      if (b.id !== id) return b;
      const opts = b.options || [];
      const correct = opts[optionIdx];
      return { ...b, accepted_answers: correct ? [correct] : [] };
    });
    setBlanks(nextBlanks);
    emit(promptHtml, nextBlanks);
  };

  const handleRemoveBlank = (id) => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const idx = blotIndexById(editor, id);
    if (idx >= 0) {
      editor.deleteText(idx, 1, 'user');
    } else {
      const nextBlanks = blanksRef.current.filter((b) => b.id !== id);
      setBlanks(nextBlanks);
      emit(promptHtml, nextBlanks);
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
      ],
    }),
    []
  );

  const formats = useMemo(
    () => ['bold', 'italic', 'underline', 'list', 'bullet', 'cloze-blank'],
    []
  );

  return (
    <Box>
      <Flex mb={2} gap={2} alignItems="center" flexWrap="wrap">
        <Button
          size="sm"
          bg="#574feb"
          color="white"
          _hover={{ bg: '#3c3489' }}
          onClick={handleMarkBlank}
          leftIcon={<span aria-hidden>＋</span>}
        >
          Marcar como espacio
        </Button>
        <Text fontSize="xs" color="gray.500">
          Selecciona una palabra y haz clic. Para borrar un espacio, Backspace sobre el chip o el botón × de su fila.
        </Text>
      </Flex>

      <Box border="1px solid" borderColor="gray.200" borderRadius="md">
        <Suspense fallback={<Box p={3} fontSize="sm" color="gray.500">Cargando editor…</Box>}>
          {isReady && (
            <LazyReactQuill
              ref={quillRef}
              theme="snow"
              value={promptHtml}
              onChange={handleHtmlChange}
              modules={modules}
              formats={formats}
            />
          )}
        </Suspense>
      </Box>

      <Box mt={4}>
        <Text fontSize="sm" fontWeight="500" mb={2}>
          Configuración de espacios
        </Text>
        {blanks.length === 0 && (
          <Text fontSize="xs" color="gray.500">
            Aún no marcaste ningún espacio. Selecciona una palabra y haz clic en "Marcar como espacio".
          </Text>
        )}
        {blanks.map((b) => {
          const isSelect = b.mode === 'select';
          return (
            <Box
              key={b.id}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              p={3}
              mb={2}
              bg="gray.50"
            >
              <Flex gap={2} alignItems="center" mb={2}>
                <Badge
                  bg="#eeedfe"
                  color="#3c3489"
                  px={2}
                  py={1}
                  borderRadius="md"
                  minW="32px"
                  textAlign="center"
                >
                  {b.id}
                </Badge>
                <Select
                  size="sm"
                  width="200px"
                  value={b.mode}
                  onChange={(e) => handleChangeMode(b.id, e.target.value)}
                  bg="white"
                >
                  <option value="text">Texto libre</option>
                  <option value="select">Opciones</option>
                </Select>
                <Box flex="1" />
                <IconButton
                  aria-label="Eliminar espacio"
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  icon={<span aria-hidden>×</span>}
                  onClick={() => handleRemoveBlank(b.id)}
                />
              </Flex>

              {!isSelect && (
                <>
                  <Input
                    size="sm"
                    placeholder="literaria, producción literaria"
                    defaultValue={(b.accepted_answers || []).join(', ')}
                    onBlur={(e) => handleUpdateTextAnswers(b.id, e.target.value)}
                    bg="white"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Separa varias respuestas válidas con coma. Se normalizan tildes y mayúsculas.
                  </Text>
                </>
              )}

              {isSelect && (
                <Box>
                  <Text fontSize="xs" color="gray.600" mb={2}>
                    Marca cuál opción es la correcta. El estudiante verá las opciones barajadas.
                  </Text>
                  <RadioGroup
                    value={(b.accepted_answers && b.accepted_answers[0]) || ''}
                  >
                    <Stack spacing={1.5}>
                      {(b.options || []).map((opt, idx) => (
                        <Flex key={`${b.id}-opt-${idx}`} gap={2} alignItems="center">
                          <Radio
                            isChecked={
                              !!opt &&
                              b.accepted_answers &&
                              b.accepted_answers[0] === opt
                            }
                            onChange={() => handleSetCorrect(b.id, idx)}
                            colorScheme="green"
                          />
                          <Input
                            size="sm"
                            placeholder={`Opción ${idx + 1}`}
                            defaultValue={opt}
                            onBlur={(e) => handleUpdateOptionText(b.id, idx, e.target.value)}
                            bg="white"
                          />
                          <IconButton
                            aria-label="Eliminar opción"
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            icon={<span aria-hidden>×</span>}
                            onClick={() => handleRemoveOption(b.id, idx)}
                          />
                        </Flex>
                      ))}
                    </Stack>
                  </RadioGroup>
                  <Button
                    size="xs"
                    mt={2}
                    variant="outline"
                    borderStyle="dashed"
                    borderColor="#574feb"
                    color="#574feb"
                    onClick={() => handleAddOption(b.id)}
                  >
                    ＋ Añadir opción
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
